import React from 'react';
import { AlertTriangle, CheckCircle, Info, AlertCircle } from 'lucide-react';

export interface AlertModalProps {
  title: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  onClose: () => void;
}

const AlertModal: React.FC<AlertModalProps> = ({
  title,
  message,
  type = 'info',
  onClose,
}) => {
  const getTypeConfig = () => {
    switch (type) {
      case 'success':
        return {
          bgGradient: 'from-green-600 to-emerald-600',
          icon: CheckCircle,
          iconColor: 'text-green-600',
        };
      case 'error':
        return {
          bgGradient: 'from-red-600 to-pink-600',
          icon: AlertTriangle,
          iconColor: 'text-red-600',
        };
      case 'warning':
        return {
          bgGradient: 'from-amber-600 to-orange-600',
          icon: AlertCircle,
          iconColor: 'text-amber-600',
        };
      default:
        return {
          bgGradient: 'from-blue-600 to-indigo-600',
          icon: Info,
          iconColor: 'text-blue-600',
        };
    }
  };

  const config = getTypeConfig();
  const IconComponent = config.icon;

  return (
    <div className="max-w-md mx-auto">
      {/* Header */}
      <div className={`bg-gradient-to-r ${config.bgGradient} px-6 py-4 rounded-t-2xl`}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
            <IconComponent className="text-white" size={24} />
          </div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
      </div>

      {/* Body */}
      <div className="p-6 bg-white">
        <div className="flex items-start gap-3">
          <IconComponent className={`${config.iconColor} mt-0.5`} size={20} />
          <div>
            <p className="text-sm text-slate-900 font-medium">{message}</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-slate-50 rounded-b-2xl flex justify-end">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-600 text-white font-medium rounded-xl hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
        >
          OK
        </button>
      </div>
    </div>
  );
};

export default AlertModal;